// Onglets « Délais », « Tarif », « Document final », « Conservation » et « Textes usager » de
// l'éditeur de version.
import { CANAUX_NOTIFICATION_DEMARCHE, MOMENTS_PAIEMENT, STATUTS_USAGER, UNITES_DELAI, type ContenuVersion, type StatutUsager } from '@cc/contrats';
import { Bandeau, CaseACocher, Champ, Selection, ZoneTexte } from '@cc/ui';
import { LIBELLES_CANAUX, LIBELLES_MOMENT_PAIEMENT, useModelesDocuments, useReglesConservation } from '../contenu';
import type { ProprietesOnglet } from './OngletIdentite';
import styles from '../demarches.module.css';

type Delai = { jours: number; unite: 'calendaires' | 'ouvres' } | null;

function ChampDelai({ libelle, valeur, onChanger, lectureSeule, aide }: { libelle: string; valeur: Delai; onChanger: (d: Delai) => void; lectureSeule: boolean; aide?: string }) {
  return (
    <fieldset className={styles.sousEditeur}>
      <legend>{libelle}</legend>
      <CaseACocher libelle="Délai défini" checked={valeur !== null} disabled={lectureSeule} onChange={(e) => onChanger(e.target.checked ? { jours: 30, unite: 'calendaires' } : null)} />
      {valeur ? (
        <div className={styles.deuxChamps}>
          <Champ libelle="Nombre de jours" type="number" min={1} max={3650} value={valeur.jours} disabled={lectureSeule} required onChange={(e) => onChanger({ ...valeur, jours: Number(e.target.value) || 1 })} />
          <Selection libelle="Unité" value={valeur.unite} disabled={lectureSeule} options={UNITES_DELAI.map((u) => ({ valeur: u, libelle: u === 'ouvres' ? 'Jours ouvrés (calendrier de la collectivité)' : 'Jours calendaires' }))} onChange={(e) => onChanger({ ...valeur, unite: e.target.value as 'calendaires' | 'ouvres' })} />
        </div>
      ) : null}
      {aide ? <p className={styles.aideDiscrete}>{aide}</p> : null}
    </fieldset>
  );
}

export function OngletDelaisTarif({ contenu, onChanger, lectureSeule, partie }: ProprietesOnglet & { partie: 'delais' | 'tarif' }) {
  const d = contenu.delais;
  const t = contenu.tarif;
  const majDelais = (patch: Partial<ContenuVersion['delais']>) => onChanger({ ...contenu, delais: { ...d, ...patch } });
  const majTarif = (patch: Partial<ContenuVersion['tarif']>) => onChanger({ ...contenu, tarif: { ...t, ...patch } });
  if (partie === 'tarif') {
    return (
      <div className={styles.pile}>
        <Bandeau type="info">Le paiement des démarches arrive avec le lot des recettes (L2). Le montant indicatif sert aux calculs du formulaire et à l'affichage, marqué « tarif fictif de présentation » hors production ; seul le moment « aucun paiement » est publiable.</Bandeau>
        <div className={styles.deuxChamps}>
          <Champ id="version-rubrique" libelle="Rubrique tarifaire" className="cc-mono" value={t.rubrique ?? ''} disabled={lectureSeule} facultatif aide="Code en minuscules, par exemple redevance_emplacement ; référencée par les calculs « tarif »." onChange={(e) => majTarif({ rubrique: e.target.value.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_') || null })} />
          <Champ id="version-montant" libelle="Montant indicatif (francs CFA)" type="number" min={0} step={1} value={t.montant_indicatif ?? ''} disabled={lectureSeule} facultatif onChange={(e) => majTarif({ montant_indicatif: e.target.value === '' ? null : Math.max(0, Math.round(Number(e.target.value))) })} />
        </div>
        <Selection id="version-paiement" libelle="Moment du paiement" value={t.moment_paiement} disabled={lectureSeule} options={MOMENTS_PAIEMENT.map((m) => ({ valeur: m, libelle: LIBELLES_MOMENT_PAIEMENT[m] ?? m }))} onChange={(e) => majTarif({ moment_paiement: e.target.value as ContenuVersion['tarif']['moment_paiement'] })} />
      </div>
    );
  }
  return (
    <div className={styles.pile}>
      <ChampDelai libelle="Délai réglementaire" valeur={d.reglementaire} lectureSeule={lectureSeule} onChanger={(v) => majDelais({ reglementaire: v })} aide="Délai opposable à l'administration ; prolongé des suspensions (attente de complément). À confirmer par l'administration pour chaque démarche." />
      <ChampDelai libelle="Délai interne" valeur={d.interne} lectureSeule={lectureSeule} onChanger={(v) => majDelais({ interne: v })} aide="Objectif de traitement du service : un dossier qui le dépasse apparaît « en retard »." />
      <fieldset className={styles.sousEditeur}>
        <legend>Compléments non fournis</legend>
        <div className={styles.deuxChamps}>
          <Champ libelle="Relance de l'usager après (jours)" type="number" min={1} max={365} value={d.relance_complement_jours} disabled={lectureSeule} required onChange={(e) => majDelais({ relance_complement_jours: Number(e.target.value) || 1 })} />
          <Champ libelle="Classement sans suite après (jours)" type="number" min={2} max={730} value={d.classement_sans_suite_jours} disabled={lectureSeule} required aide="Doit suivre la relance." erreur={d.classement_sans_suite_jours <= d.relance_complement_jours ? 'Le classement sans suite suit la relance' : undefined} onChange={(e) => majDelais({ classement_sans_suite_jours: Number(e.target.value) || 2 })} />
        </div>
      </fieldset>
    </div>
  );
}

export function OngletDocument({ contenu, onChanger, lectureSeule, partie }: ProprietesOnglet & { partie: 'document' | 'conservation' }) {
  const modeles = useModelesDocuments(partie === 'document');
  const regles = useReglesConservation(partie === 'conservation');
  const maj = (patch: Partial<ContenuVersion>) => onChanger({ ...contenu, ...patch });
  const modelesPublies = (modeles.data ?? []).filter((m) => m.publie);
  if (partie === 'conservation') {
    return (
      <div className={styles.pile}>
        <Selection
          id="version-conservation"
          libelle="Règle de conservation des pièces et documents"
          value={contenu.regle_conservation_id ?? ''}
          disabled={lectureSeule}
          optionVide="Règle par défaut du type de document"
          options={(regles.data ?? []).map((r) => ({ valeur: r.id, libelle: `${r.type_document} : ${r.duree_annees === null ? 'durée à définir' : `${r.duree_annees} an${r.duree_annees > 1 ? 's' : ''}`}, sort final ${r.sort_final}` }))}
          aide={regles.error ? 'Règles non lisibles (droit sur les paramètres requis).' : 'Les durées « à définir » attendent l\'application de l\'ordonnance sur l\'archivage (H3-09).'}
          onChange={(e) => maj({ regle_conservation_id: e.target.value || null })}
        />
      </div>
    );
  }
  return (
    <div className={styles.pile}>
      <fieldset className={styles.sousEditeur}>
        <legend>Document final</legend>
        <Selection
          id="version-modele"
          libelle="Modèle du document délivré à la décision"
          value={contenu.modele_document_code ?? ''}
          disabled={lectureSeule}
          optionVide="Aucun document généré"
          options={modelesPublies.some((m) => m.code === contenu.modele_document_code) || !contenu.modele_document_code ? modelesPublies.map((m) => ({ valeur: m.code, libelle: `${m.libelle} (version ${m.version})` })) : [...modelesPublies.map((m) => ({ valeur: m.code, libelle: `${m.libelle} (version ${m.version})` })), { valeur: contenu.modele_document_code, libelle: contenu.modele_document_code }]}
          aide={modeles.error ? 'Liste des modèles indisponible (droit de configuration requis).' : 'Généré en PDF après la décision, avec un code de vérification public.'}
          onChange={(e) => maj({ modele_document_code: e.target.value || null })}
        />
      </fieldset>
      <fieldset className={styles.sousEditeur}>
        <legend>Identité et notifications</legend>
        <CaseACocher libelle="Identité vérifiée requise pour déposer (pièce vue au guichet)" checked={contenu.identite_verifiee_requise} disabled={lectureSeule} onChange={(e) => maj({ identite_verifiee_requise: e.target.checked })} />
        <div className={styles.cases}>
          {CANAUX_NOTIFICATION_DEMARCHE.map((c) => (
            <CaseACocher
              key={c}
              libelle={LIBELLES_CANAUX[c] ?? c}
              checked={contenu.canaux_notification.includes(c)}
              disabled={lectureSeule || (contenu.canaux_notification.length === 1 && contenu.canaux_notification.includes(c))}
              onChange={(e) => maj({ canaux_notification: e.target.checked ? [...contenu.canaux_notification, c] : contenu.canaux_notification.filter((x) => x !== c) })}
            />
          ))}
        </div>
        <p className={styles.aideDiscrete}>Canaux par lesquels l'usager est informé des changements de statut ; au moins un canal.</p>
      </fieldset>
    </div>
  );
}

export function OngletTextes({ contenu, onChanger, lectureSeule }: ProprietesOnglet) {
  const maj = (statut: StatutUsager, texte: string) => {
    const textes = { ...contenu.textes_statuts };
    if (texte.trim()) textes[statut] = texte;
    else delete textes[statut];
    onChanger({ ...contenu, textes_statuts: textes });
  };
  return (
    <div className={styles.pile}>
      <p className={styles.aideDiscrete}>Texte affiché à l'usager dans son suivi pour chaque statut, en plus du libellé du statut. Il explique ce qui se passe et si une action est attendue.</p>
      {(Object.keys(STATUTS_USAGER) as StatutUsager[]).map((s) => (
        <ZoneTexte key={s} id={`texte-${s}`} libelle={`Statut « ${STATUTS_USAGER[s]} »`} value={contenu.textes_statuts[s] ?? ''} rows={2} disabled={lectureSeule} facultatif maxLength={1000} onChange={(e) => maj(s, e.target.value)} />
      ))}
    </div>
  );
}
